// Right-anchored drawer that hosts the per-SKU (or platform-wide)
// FeatureTable. Replaces the inline tables that used to live inside
// every SkuCard + the PlatformFeaturesCard on the licensing detail page
// (~280 LoC of inline table rendering → 1 button + 1 drawer per card).
//
// Chrome matches the WAF Exception side-drawer (Modal + Slide pattern)
// — same as the upgraded design-system DetailDrawer/FormDrawer. The
// FeatureTable needs a wider drawer than the shared primitives (its
// Status column + numeric input cause horizontal scroll under 640px),
// so this wrapper bumps minWidth from 480 → 640 while keeping the
// 33% responsive sizing.
//
// Closes on the X button, ESC key, and backdrop click.

import React from "react";
import { Modal, Slide, Box, Typography, IconButton, Stack } from "@mui/material";
import { ReactComponent as CloseIcon } from "../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as FeaturesIcon } from "../../../../../vendor/_new/icon/sidebar/settings.svg";

import { TOKENS } from "../design";
import FeatureTable from "./FeatureTable";

const FeaturesDrawer = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  statusPill,
  skuId,
  rows,
  loading,
  error,
  onMutated,
  onError,
}) => {
  const total = (rows || []).length;
  const overrides = (rows || []).filter((r) => r.is_overridden).length;
  const renderedIcon = icon ?? <FeaturesIcon />;
  const renderedSubtitle = subtitle || "Manage feature toggles per SKU";

  return (
    <Modal
      open={!!open}
      onClose={onClose}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" } },
      }}
    >
      <Slide direction="left" in={!!open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 640,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
          }}
        >
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
            sx={{ px: 4, pt: 4, pb: 3, flexShrink: 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
              {renderedIcon ? (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#6371F7",
                      "& > svg": { width: 32, height: 32, color: "#6371F7" },
                    }}
                  >
                    {renderedIcon}
                  </Box>
                </Box>
              ) : null}
              <Stack sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                  <Typography
                    sx={{
                      fontWeight: "medium",
                      fontSize: 20,
                      lineHeight: "30px",
                      color: TOKENS.fg1,
                    }}
                  >
                    {title}
                  </Typography>
                  {statusPill ? <Box>{statusPill}</Box> : null}
                </Stack>
                {renderedSubtitle ? (
                  <Typography
                    sx={{
                      fontSize: 12,
                      lineHeight: "18px",
                      color: TOKENS.fg2,
                    }}
                  >
                    {renderedSubtitle}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
            <IconButton onClick={onClose} aria-label="Close drawer" sx={{ p: 0, color: TOKENS.fg2 }}>
              <CloseIcon style={{ color: TOKENS.fg2 }} />
            </IconButton>
          </Stack>

          {/* Sub-header — counts */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 4,
              py: 1.25,
              background: TOKENS.bgSunken,
              borderTop: `1px solid ${TOKENS.borderSubtle}`,
              borderBottom: `1px solid ${TOKENS.borderSubtle}`,
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2 }}>
              {total} feature{total === 1 ? "" : "s"}
            </Typography>
            <Typography
              sx={{
                fontSize: 12.5,
                color: overrides > 0 ? TOKENS.primaryInk : TOKENS.fg3,
                fontWeight: overrides > 0 ? 600 : 400,
              }}
            >
              {overrides} override{overrides === 1 ? "" : "s"}
            </Typography>
          </Box>

          {/* Body — the FeatureTable. We hide its own internal header (the
              drawer header replaces it) but keep the "Clear all" footer
              since it's a destructive action that wants a confirm modal. */}
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <FeatureTable
              skuId={skuId}
              rows={rows}
              loading={loading}
              error={error}
              onMutated={onMutated}
              onError={onError}
              showHeader={false}
              showClearAllFooter={true}
            />
          </Box>
        </Stack>
      </Slide>
    </Modal>
  );
};

export default FeaturesDrawer;
